import { Map, View, Feature } from 'ol';
import { Vector as VectorLayer, Tile as TileLayer, Image as ImageLayer } from 'ol/layer';
import { Vector as VectorSource, OSM as OSMSource, ImageWMS } from 'ol/source';
import { Extent} from 'ol/extent';
import { WKT } from 'ol/format';
import GeometryType from 'ol/geom/GeometryType';
import { defaults as ControlDefaults, Control, Attribution, Zoom, ZoomToExtent, ScaleLine, MousePosition, OverviewMap } from 'ol/control';
import * as toastr from "toastr";
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import Projection from 'ol/proj/Projection';
import { GridReferences } from '../Utility/GridReferences';
import Overlay from 'ol/Overlay';
import { IAPIResult } from '../Models/UtilityModels';
import { MapStyles } from './MapStyles';
import { MapConfigModel, MapInitialState, MapLayerGroupType } from '../Models/MapConfigModels';
import { MapLayersHelper } from './MapLayersHelper';
import { FeatureType, ICAMSKey, MapInteractionType, IMapInteractionConfig, TextPack } from '../Models/UtilityModels';
import * as html2canvas from 'html2canvas'
import * as events from '../Bus/Bus';
import * as AuthModel from '../Models/AuthModel';
import { LocalStorageSettingsModel } from '../Models/LocalStorageSettingsModel';
import { Utilities, ModalAction } from '../Utility/Utilities';
import { MapLegend } from './MapLegend';
import { CAMSMap, CAMSVectorSource } from '../../Extensions/CAMSOLExtensions';
import { MapZoom } from './MapZoom';
import { MapInteraction } from './MapInteraction';

declare var G_IsMobile: Boolean;
// ----------------------------------------------------------- //
export type exportMapToPngCallback = (blob: Blob, extent: Extent) => any;

export enum OptionalTracingLayer {
    routeLineLayer,
    landPolygonLayer
}

// MAIN CLASS
export class MainMap {

    // ----------------------------------------------------------- //
    // GLOBAL VARS
    public map: CAMSMap;
    public mapLayersHelper: MapLayersHelper;
    public MapZoom: MapZoom;
    public MapInteraction: MapInteraction;

    private initialised: boolean = false;
    private authModel: AuthModel.IAuthModel = { policies: [] };
    public mapConfigModel: MapConfigModel;
    private mapLegend: MapLegend;
    public mapOverlay: Overlay;
    public MapStyles: MapStyles = new MapStyles();

    private additionalMapLayersList = [];
    public selectedFeaturesList: Feature[] = [];
    public highlightedFeaturesList: Feature[] = [];
    public editLayer: VectorLayer;
    private selectListLayer: VectorLayer;
    public highlightLayer: VectorLayer;
    public locationLayer: VectorLayer;
    private gridLayer: VectorLayer;
    private selectedFeaturesLayer: VectorLayer;
    private baseLayersList = [];
   
    public textPackKeys: Record<string, string> = {
        OfficerMapTextPack_TS_Copyright: "TS_Copyright",
        OfficerMapTextPack_TS_ZoomIn: "TS_ZoomIn",
        OfficerMapTextPack_TS_ZoomOut: "TS_ZoomOut",
        OfficerMapTextPack_TS_ZoomToAuthority: "TS_ZoomToAuthority",
        OfficerMapTextPack_TS_Toastr_LocationError: "TS_Toastr_LocationError",
        OfficerMapTextPack_TS_Toastr_FeatureZoomError: "TS_Toastr_FeatureZoomError",
        OfficerMapTextPack_TS_Toastr_LatLonZoomError: "TS_Toastr_LatLonZoomError",
        OfficerMapTextPack_TS_Toastr_GridRefZoomError: "TS_Toastr_GridRefZoomError",
        OfficerMapTextPack_TS_Toastr_WktError: "TS_Toastr_WktError",
        OfficerMapTextPack_TS_Toastr_GeomUpdate: "TS_Toastr_GeomUpdate",
        OfficerMapTextPack_TS_Toastr_GeomUpdateError: "TS_Toastr_GeomUpdateError",
        OfficerMapTextPack_TS_Toastr_PlacePointCloserError: "TS_Toastr_PlacePointCloserError",
        OfficerMapTextPack_TS_Toastr_ZoomCloserError:"TS_Toastr_ZoomCloserError"

    };

    // ----------------------------------------------------------- //
    // Defaults
    private startingMapExtent = [0, 0, 700000, 1100000];
    public setMapFilterAnnotationUID: Function = function (annotationUID: number) { const self = this; self.mapLayersHelper.setMapFilterAnnotationUID(annotationUID); }

    // ----------------------------------------------------------- //
    //Constructor
    public constructor(protected mapDOMID, authModel: AuthModel.IAuthModel, mapConfigModel: MapConfigModel) {
        const self = this;
        // Register projections
        proj4.defs('EPSG:27700', '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs');
        proj4.defs('EPSG:3857', '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +a=6378137 +b=6378137 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
        register(proj4);

        self.MapZoom = new MapZoom(this);
        self.MapStyles.init(mapConfigModel);
        self.authModel = authModel;
        self.mapConfigModel = mapConfigModel;
        self.initEvents();
        self.initMap(mapDOMID);

        // Setup map legend btn
        $('#mapLegendShowBtn').on('click', function () { self.showLegendModal(); });
        self.mapLegend = new MapLegend(self.MapStyles);
    }

    private initEvents = (): void => {
        const self = this;
        $('#layersCloseBtn').on("click", function () {
            events.bus.publish(events.eventResetRightPanels());
        });
        $('#mapButtonsCloseBtn').on("click", function () { self.MapButtonCancel(); });
        $('#mapButtons_SaveBtn').on("click", function () { self.MapButtonSave(null); });
        $('#mapButtons_CancelBtn').on("click", function () { self.MapButtonCancel(); });
    }

    // ----------------------------------------------------------- //
    //MAP
    private initMap = (mapDOMID: string): void => {
        if (!this.initialised) {
            console.log("Initialising main map");
            let self = this;
            let listOfLayers = [];
            
            // Initial the maplayers helper
            self.mapLayersHelper = new MapLayersHelper(self.authModel, self.mapConfigModel, self.MapStyles);

            // Get base layers
            this.baseLayersList = self.mapLayersHelper.createBaseLayersFromConfig(self.mapConfigModel.baseLayers);
            listOfLayers = listOfLayers.concat(this.baseLayersList);
            
            // Get Additional map layers
            self.additionalMapLayersList = self.mapLayersHelper.createAdditionalLayersFromConfig(self.mapConfigModel.additionalLayers);
            listOfLayers = listOfLayers.concat(self.additionalMapLayersList);

            // Create the Land layers
            self.mapLayersHelper.setupLandLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Land).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Place highlight and select layer at the bottom, but above any polygons
            this.highlightLayer = this.createHighlightLayer(); listOfLayers.push(this.highlightLayer);
            this.selectedFeaturesLayer = this.createSelectedFeaturesLayer(); listOfLayers.push(this.selectedFeaturesLayer);

            // Create the TRO layers
            self.mapLayersHelper.setupTROLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.TRO).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the Route layers
            self.mapLayersHelper.setupRouteLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Route).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the LegalRoute layers
            self.mapLayersHelper.setupLegalRouteLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalRoute).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the LegalFeature layers
            self.mapLayersHelper.setupLegalFeatureLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.LegalFeature).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the Infra layers
            self.mapLayersHelper.setupInfraLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Infra).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the Infra Historic layers
            self.mapLayersHelper.setupInfraHistoricLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.InfraHistoric).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create the Maint layers
            self.mapLayersHelper.setupMaintLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Maint).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create MyMaint layers
            if (this.mapConfigModel.contactRef != null) {
                self.mapLayersHelper.setupMyMaintLayers();
                self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.MyMaint).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));
            }

            // Create the Annotation layers
            self.mapLayersHelper.setupAnnotationLayers();
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == MapLayerGroupType.Annotation).forEach(m => listOfLayers = listOfLayers.concat(m.olLayers));

            // Create GPS location layer
            this.locationLayer = this.createLocationLayer(); listOfLayers.push(this.locationLayer);

            // Create grid layer
            this.gridLayer = self.mapLayersHelper.createGridLayer(); listOfLayers.push(this.gridLayer);

            // Add edit layer
            this.editLayer = this.createEditLayer(); listOfLayers.push(this.editLayer);
            
            // Get local map details
            const localMapDetails = this.getLocalMapDetails();

            //create the map controls collection
            let controls = this.createMapControls(this.startingMapExtent as any);//TODO: replace hard-coded with configurable value

            // create overlay
            self.mapOverlay = new Overlay({
                element: $('#mainMapPopup')[0],
                autoPan: true,
                autoPanAnimation: {
                    duration: 250,
                }
            });

            // create projection
            let projection = new Projection({
                code: self.mapConfigModel.mapInitialState.mapBaseProjection,
                units: 'm',
            });


            // Create the map
            this.map = new CAMSMap({
                target: mapDOMID,
                controls: controls,
                layers: listOfLayers,
                overlays: [self.mapOverlay],
                maxTilesLoading: 8,
                view: new View({
                    center: localMapDetails.center as any,
                    zoom: localMapDetails.zoom,
                    projection: projection,
                    minZoom: self.mapConfigModel.mapInitialState.minZoom,
                    maxZoom: self.mapConfigModel.mapInitialState.maxZoom,
                    enableRotation: false,
                    extent: self.startingMapExtent as any,
                })
            });
          
            //Bind Map Layers
            self.bindMapLayersToUI();

            // Set map grid
            $('[data-maplayergrid]').find('[name="btnSwitch"]').on('click', function () {
                let checked = $(this).find('input').prop('checked');
                $(this).find('input').prop('checked', !checked);
                self.gridLayer.setVisible(!checked);
            });
            
            //Bind Base Layers
            $('[data-baselayer]').each(function () {
                $(this).find('input').on('change', function () {
                    let checked = $(this).prop('checked');
                    self.baseLayersList.forEach(l => {
                        if (l.DisplayName == $(this).prop('id')) {
                            l.setVisible(checked);
                        } else {
                            l.setVisible(!checked);
                        }
                    });
                });
            });

            // Map Interactions
            this.MapInteraction = new MapInteraction(this);
            this.MapInteraction.init();

            // Map events
            this.map.on('moveend', this.mapMoveEnd);
            this.map.getView().on('change:resolution', this.mapZoomEnd);
            
            // Add this usedful function to the map object
            this.map.getFeatureFromMap = function (data: ICAMSKey) { return self.getFeatureFromMap(data); };
            this.map.convertFeatureToCAMSKey = function (feature: Feature) { return Utilities.convertFeatureToCAMSKey(feature); };

            // CURRENTLY DISABLED
            // Setup the annotation conntext menu
            //this.setUpContextMenu();

            this.initialised = true;
        }
    }

    // ----------------------------------------------------------- //

    // ----------------------------------------------------------- //
    //ZOOM Functions
    public zoomToMyLocation = (clearHighlights: boolean): void => {
        this.MapZoom.zoomToMyLocation(clearHighlights);
    }

    public zoomToFeatureAlreadyInMap = (data: ICAMSKey, clearHighlights: boolean): void => {
        this.MapZoom.zoomToFeatureAlreadyInMap(data, clearHighlights);
    }

    public zoomToLocationAndThenHighlightFeature = (data: ICAMSKey, clearHighlights: boolean, callback: Function): void => {
        this.MapZoom.zoomToLocationAndThenHighlightFeature(data, clearHighlights, callback);
    }

    public zoomToWKTandHighlight = (wktArr: Array<string>, clearHighlights: boolean, addWKTToMap: boolean, featureType: FeatureType): void => {
        this.MapZoom.zoomToWKTandHighlight(wktArr, clearHighlights, addWKTToMap, featureType);
    }

    public zoomToLatLon = (data: ICAMSKey, clearHighlights: boolean, placeMarker: boolean): void => {
        this.MapZoom.zoomToLatLon(data, clearHighlights, placeMarker);
    }

    public zoomToGridRef = (data: any, clearHighlights: boolean, placeMarker: boolean): void => {
        this.MapZoom.zoomToGridRef(data, clearHighlights, placeMarker);
    }

    public zoomToScale = (scale: number): void => {
        this.MapZoom.zoomToScale(scale);
    }

    // ----------------------------------------------------------- //
    //API Functions
    public getFeatureFromAPIAndShowInPanel = (data: ICAMSKey): void => {
        // Get the data from the API
        const self = this;

        if (data.featureType == FeatureType.Maint) {
            $(document).trigger("openMaintDetailsPanel", data);
        }

        if (data.featureType == FeatureType.Infra) {
            $(document).trigger("openInfraDetailsPanel", data);
        }

        if (data.featureType == FeatureType.InfraHistoric) {
            alert("tbc");
        }

        if (data.featureType == FeatureType.Route) {
            $(document).trigger("openRouteDetailsPanel", data);
        }

        if (data.featureType == FeatureType.Land) {
            $(document).trigger("openLandDetailsPanel", data);
        }

        if (data.featureType == FeatureType.LegalEvent) {
            alert('tbc MainMap.getFeatureFromAPIAndShowInPanel.ts');
        }

        if (data.featureType == FeatureType.LegalFeature) {
            alert('tbc MainMap.getFeatureFromAPIAndShowInPanel.ts');
        }
    }

    public clearAllHighlights = (): void => {
        this.highlightLayer.getSource().clear();
        this.highlightedFeaturesList = [];
        this.MapInteraction.selectInteraction.getFeatures().clear();
        this.editLayer.getSource().clear();
    }

    public clearEditLayer = (): void => {
        this.editLayer.getSource().clear();
    }

    public GetSelectedFeatures = (callback: Function): Array<Feature> => {
        if (callback != null) callback(this.selectedFeaturesList);
        return this.selectedFeaturesList;
    }

    public clearAllSelectedFeatures = (): void => {
        this.selectedFeaturesLayer.getSource().clear();
        this.selectedFeaturesList = [];
        this.MapInteraction.selectInteraction.getFeatures().clear();
    }

    public mapLayerVisibilitySwitch = (mapLayerGroupType: MapLayerGroupType, makeVisible: Boolean): void => {
        const self = this;
        var el = $('[data-maplayergrouptype="' + mapLayerGroupType + '"]');
        el.find('input').prop('checked', makeVisible);
        self.toggleMapLayerVisibility(el);
    }

    // ----------------------------------------------------------- //
    //Map Functions
    public getFeatureFromMap = (data: ICAMSKey): Feature => {
        const self = this;
        let feature: Feature = null;

        if (data.featureType == FeatureType.Maint) {
            search("MaintCode", MapLayerGroupType.Maint);
        }
        else if (data.featureType == FeatureType.Route) {
            search("RouteCode", MapLayerGroupType.Route);
        }
        else if (data.featureType == FeatureType.Path) {
            search("RouteCode", MapLayerGroupType.Route);
        }
        else if (data.featureType == FeatureType.Infra) {
            search("InfraCode", MapLayerGroupType.Infra);
        }
        else if (data.featureType == FeatureType.InfraHistoric) {
            search("InfraCode", MapLayerGroupType.InfraHistoric);
        }
        else if (data.featureType == FeatureType.LegalEvent) {
            search("LegalRoute", MapLayerGroupType.LegalRoute);
        }
        else if (data.featureType == FeatureType.LegalFeature) {
            search("LegalFeature", MapLayerGroupType.LegalFeature);
        }
        else if (data.featureType == FeatureType.Land) {
            search("LandCode", MapLayerGroupType.Land);
        }
        else if (data.featureType == FeatureType.Annotation) {
            search("AnnotationGeomID", MapLayerGroupType.Annotation);
        }
        else if (data.featureType == FeatureType.TRO) {
            search("TROGeomID", MapLayerGroupType.TRO);
        }

        function search(codefield: string, type: MapLayerGroupType) {
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == type).forEach(m => {
                if (feature == null && m.olLayers != null) {
                    m.olLayers.forEach(o => {
                        if (o && o.getSource()) {
                            for (let f of o.getSource().getFeatures()) {
                                if (f.get(codefield) == data.code && feature == null) {
                                    feature = f;
                                    break;
                                }
                            }
                        }
                    });
                }
            });
        }
        return feature;
    }

    private createMapControls = (extent: [number, number, number, number]): Control[] => {
        const self = this;

        let copyright = document.createElement('span');
        copyright.innerHTML = '<span class="fa fa-copyright"></span>';
        let attribution = new Attribution({
            collapsible: true,
            collapsed: true,
            label: copyright
        });

        let globe = document.createElement('span');
        globe.innerHTML = '<span class="fa fa-globe-europe"></span>';

        let zoomToExtent = new ZoomToExtent({
            extent: extent,
            label: globe
        });

        let zoom = new Zoom({
            duration: 100,
            delta: 1
        });

        // This will hide the overview map on small screens using the bootstrap d-none d-sm-block
        var overviewMap = new OverviewMap({
            className: 'ol-overviewmap d-none d-sm-block',
            collapsible: false,
            layers: [
                new TileLayer({
                    source: new OSMSource()
                })
            ]
        });

        var scaleLine = new ScaleLine({
            minWidth: 140,
            text: true,
            bar: true,
            steps: 2
        });

        var mousePositionControl = new MousePosition({
            coordinateFormat: function (c) {
                try {
                    let osgb = new GridReferences.OSGB();
                    osgb.SetGridCoordinates(c[0], c[1]);
                    return osgb.GetGridRef(6);
                }
                catch (e) {
                    console.error(e);
                    return null;
                }
            },
            projection: self.mapConfigModel.mapInitialState.mapBaseProjection
        });

        //get translated text elements
        Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_Copyright }]).then(function (r) { attribution.set('tipLabel', r.data[0].value, false); }).catch(function (e) { console.log(e); });
        Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_ZoomIn }]).then(function (r) { zoom.set('zoomInTipLabel', r.data[0].value, false); }).catch(function (e) { console.log(e); });
        Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_ZoomOut }]).then(function (r) { zoom.set('zoomOutTipLabel', r.data[0].value, false); }).catch(function (e) { console.log(e); });
        Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_ZoomToAuthority }]).then(function (r) { zoomToExtent.set('tipLabel', r.data[0].value, false); }).catch(function (e) { console.log(e); });

        //return [zoom, attribution, zoomToExtent, new MousePosition(), new ScaleLine(), overviewMap];
        return [zoom, attribution, mousePositionControl, scaleLine];
    }

    private createEditLayer = (): VectorLayer => {
        const self = this;

        // Set data edit layer sources
        let editLayerSource = new VectorSource({ wrapX: false });
        let editLayer = new VectorLayer({
            source: editLayerSource,
            style: function (feature, resolution) {
                return MapStyles.editStyleFunction(feature as any, resolution, self);
            }
        });

        return editLayer;
    }

    public addWKTtoEditLayer = (wktStr: string, zoomToFeature: Boolean, allowedGeomTypes: Array<GeometryType>, callback: Function): void => {
        const self = this;
 
        // Create feature from WKT
        var feature: Feature = null;

        try {
            feature = self.createFeatureFromWKT(wktStr, allowedGeomTypes);

            // Add to the edit layer
            this.editLayer.getSource().clear();
            this.editLayer.getSource().addFeature(feature);
            this.MapInteraction.switchMapInteraction(MapInteractionType.ModifyInteraction, true);

            // Zoom to feature
            if (zoomToFeature == true) this.MapZoom.centreMapOnFeatures([feature]);
        }
        catch (e) { console.log(e); }

        // If there is an error, the callback will return null
        if (callback != null) callback(feature);
    }

    private mapMoveEnd = (): void => {
        const self = this;

        // Save details to localstorage
        this.setLocalMapDetails();

        this.checkZoomLayerAndUpdateMapLegend(false);
    }

    private mapZoomEnd = (): void => {
        const self = this;

    }

    private checkZoomLayerAndUpdateMapLegend = (updateLocalSettings: boolean): void => {
        const self = this;
        // Check the layer control
        $('[data-maplayergrouptype]').each(function () {
            self.toggleMapLayerVisibility($(this), updateLocalSettings);
        });    
    }

    

    private createHighlightLayer = (): VectorLayer => {
        const self = this;
        let highlightLayerSource = new VectorSource({
            wrapX: false,
            loader: function (extent, resolution, projection) {
                highlightLayerSource.clear();
                self.highlightedFeaturesList.forEach(f => {
                    const fClone = f.clone();
                    fClone.setStyle(MapStyles.highlightStyleFunction(f, resolution, self));
                    highlightLayerSource.addFeature(fClone);
                });
            }
        });

        return new VectorLayer({
            style: function (feature, resolution) {
                return MapStyles.highlightStyleFunction(feature as any, resolution, self);
            },
            source: highlightLayerSource
        });;
    }

    private createLocationLayer = (): VectorLayer => {
        const self = this;
        let locationLayerSource = new VectorSource({
            wrapX: false
        });

        return new VectorLayer({
            style: function (feature, resolution) {
                return MapStyles.locationStyleFunction(feature as any, resolution, self);
            },
            source: locationLayerSource
        });;
    }

    private createSelectedFeaturesLayer = (): VectorLayer => {
        const self = this;
        let selectedFeaturesSource = new VectorSource({
            wrapX: false,
            loader: function (extent, resolution, projection) {
                selectedFeaturesSource.clear();
                self.selectedFeaturesList.forEach(f => {
                    const fClone = f.clone();
                    fClone.setStyle(MapStyles.selectedFeaturesStyleFunction(f, resolution, self));
                    selectedFeaturesSource.addFeature(fClone);
                });
            }
        });

        return new VectorLayer({
            style: function (feature, resolution) {
                return MapStyles.selectedFeaturesStyleFunction(feature as any, resolution, self);
            },
            source: selectedFeaturesSource
        });;
    }

    public forceReloadHighlightLayer = (): void => {
        // refresh map
        this.highlightLayer.getSource().refresh();
    }

    public forceReloadSelectedFeaturesLayer = (): void => {
        // refresh map
        this.selectedFeaturesLayer.getSource().refresh();
    }

    private getLocalMapDetails = (): MapInitialState => {
        let localStorageSettings_str = window.localStorage.getItem('localStorageSettings');
        let localStorageSettings: LocalStorageSettingsModel = JSON.parse(localStorageSettings_str);
        return localStorageSettings.MapInitialState;
    }

    private setLocalMapDetails = (): void => {
        let localStorageSettings_str = window.localStorage.getItem('localStorageSettings');
        let localStorageSettings: LocalStorageSettingsModel = JSON.parse(localStorageSettings_str);

        localStorageSettings.MapInitialState.zoom = this.map.getView().getZoom();
        localStorageSettings.MapInitialState.center = this.map.getView().getCenter();

        window.localStorage.setItem('localStorageSettings', JSON.stringify(localStorageSettings));
        return;
    }

    public closePopup = (): void => {
        const self = this;
        self.mapOverlay.setPosition(undefined);
    }

    // ----------------------------------------------------------- //

    // ----------------------------------------------------------- //
    // INTERACTIONS
    public removeAllInteractions = (): void => {
        this.MapInteraction.removeAllInteractions();
    }

    public startMultiSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startMultiSelection(mapInteractionConfig);
    }

    public startSingleSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startSingleSelection(mapInteractionConfig);
    }

    public startRouteSelection = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startRouteSelection(mapInteractionConfig);
    }

    public startRectangleSelect = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startRectangleSelect(mapInteractionConfig);
    }

    public startMultiAndRectangleSelect = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startMultiAndRectangleSelect(mapInteractionConfig);
    }

    public startLocationInfo = (mapInteractionConfig: IMapInteractionConfig): void => {
        this.MapInteraction.startLocationInfo(mapInteractionConfig);
    }

    public startAdditionalLayersLocationInfo = (layer): void => {
        this.MapInteraction.startAdditionalLayersLocationInfo(layer);
    }

    public startDrawSelection = (type: GeometryType, minZoom: number, parentFeature: Feature, parentMinDistance: number, tracingLayer: OptionalTracingLayer = null): void => {
        this.MapInteraction.startDrawSelection(type, minZoom, parentFeature, parentMinDistance, tracingLayer);
    }

    public modifyFeature = (data: ICAMSKey, showPanel: Boolean = true): void => {
        this.MapInteraction.modifyFeature(data, showPanel);
    }

    // ----------------------------------------------------------- //
    // Map Button Actions
    public MapButtonSave = (callback: Function): void => {
        let editSource = this.editLayer.getSource();
        let modifyCollection = editSource.getFeatures();

        // Get the feature
        if (modifyCollection != null && modifyCollection.length == 1) {
            let feature = modifyCollection[0]

            const self = this;
            let format = new WKT();
            let wkt = format.writeGeometry(feature.getGeometry(), {
                dataProjection: 'EPSG:27700',
                featureProjection: self.mapConfigModel.mapInitialState.mapBaseProjection
            });

            let query: Object;
            let data: ICAMSKey = {};
            let url: string;
            if (feature.getProperties().MaintCode != null) {
                url = '/Maint/UpdateMaintGeom';
                query = { maintCode: feature.getProperties().MaintCode, WKT: wkt };
                data = { code: feature.getProperties().MaintCode, featureType: FeatureType.Maint };
            }
            else if (feature.getProperties().InfraCode != null) {
                url = '/Infra/UpdateInfraGeom';
                query = { infraCode: feature.getProperties().InfraCode, WKT: wkt };
                data = { code: feature.getProperties().InfraCode, featureType: FeatureType.Infra };
            }
            else if (feature.getProperties().LegalRoute != null) {
                url = '/Legal/UpdateLegalRouteGeom';
                query = { legalCode: feature.getProperties().LegalRoute, WKT: wkt };
                data = { code: feature.getProperties().LegalRoute, featureType: FeatureType.LegalEvent };
            }
            else if (feature.getProperties().LegalFeature != null) {
                url = '/Legal/UpdateLegalFeatureGeom';
                query = { legalFeatureID: feature.getProperties().LegalFeature, WKT: wkt };
                data = { code: feature.getProperties().LegalFeature, featureType: FeatureType.LegalFeature };
            }
            else if (feature.getProperties().RouteCode != null) {
                url = '/Route/UpdateRouteGeom';
                query = { routeCode: feature.getProperties().RouteCode, WKT: wkt };
                data = { code: feature.getProperties().RouteCode, featureType: FeatureType.Route };
            }
            else if (feature.getProperties().LandCode != null) {
                url = '/Land/UpdateLandGeom';
                query = { landCode: feature.getProperties().LandCode, WKT: wkt };
                data = { code: feature.getProperties().LandCode, featureType: FeatureType.Land };
            }
            else if (feature.getProperties().AnnotationGeomID != null) {
                url = '/Annotations/UpdateAnnotationGeom';
                query = { UID: feature.getProperties().AnnotationGeomID, WKT: wkt };
                data = { code: feature.getProperties().AnnotationGeomID, featureType: FeatureType.Annotation };
            }
            else if (feature.getProperties().TROGeomID != null) {
                url = '/TRO/UpdateTROGeomOnly';
                query = { TROGeomID: feature.getProperties().TROGeomID, WKT: wkt };
                data = { code: feature.getProperties().TROGeomID, featureType: FeatureType.TRO };
            }

            if (url != undefined) {
                $.post(url, query,
                    function (result: IAPIResult) {
                        try {
                            if (result.success == true) {
                                self.editLayer.getSource().clear();
                                events.bus.publish(events.eventResetAllPanels());
                                events.bus.publish(events.eventRemoveAllMapInteractions());
                                events.bus.publish(events.eventResetAllButtons(true));
                                events.bus.publish(events.eventRefreshMapLayer(data.featureType));
                                events.bus.publish(events.eventMapGeometrySaved(data));
                                Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_Toastr_GeomUpdate }]).then(function (r) { toastr.success(r.data[0].value); }).catch(function (e) { console.log(e); });
                            }
                            else {
                                Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.textPackKeys.OfficerMapTextPack_TS_Toastr_GeomUpdateError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
                            }
                        }
                        catch (err) {
                            console.log(err);
                            alert(err);
                        }
                        finally {
                            if (callback != null) {
                                callback(result.success);
                            }
                        }
                    }
                ).fail(function (xhr) {
                    if (xhr.status === 401) {//redirect to login in unauthorised
                        window.location.href = '/account/login?ReturnUrl=' + encodeURIComponent(window.location.pathname);
                    }
                });
            }
            else {
                if (callback != null) {
                    callback(false);
                }
            }
        }
    }

    public MapButtonCancel = (): void => {
        let modifyCollection = this.editLayer.getSource().getFeatures();
        if (modifyCollection != null && modifyCollection.length == 1) {
            let ft = Utilities.getFeatureTypeFromFeature(modifyCollection[0]);
            events.bus.publish(events.eventRefreshMapLayer(ft));
        }

        this.editLayer.getSource().clear();
        events.bus.publish(events.eventResetAllPanels());
        events.bus.publish(events.eventRemoveAllMapInteractions());
        //events.bus.publish(events.eventResetAllButtons(true));
        events.bus.publish(events.eventMapGeometrySaved(null));
    }

    
    public refreshMapLayer = (featureType: FeatureType): void => {
        var mapLayerGroupType = Utilities.convertFeatureTypeToMapLayerGroupType(featureType);
        if (mapLayerGroupType != null) {
            this.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == mapLayerGroupType).forEach(m => {
                if (m.olLayers != null) {
                    m.olLayers.forEach(o => {
                        if ((o && o.getSource() as CAMSVectorSource)) (o.getSource() as CAMSVectorSource).forceRefresh();
                    });
                }
            });
        }
        else {
            this.mapConfigModel.coreLayers.forEach(m => {
                if (m.olLayers != null) {
                    m.olLayers.forEach(o => {
                        if ((o && o.getSource() as CAMSVectorSource)) (o.getSource() as CAMSVectorSource).forceRefresh();
                    });
                }
            });
        }
    }

    public setVisibleMapLayer = (featureType: FeatureType, visible: boolean): void => {
        var mapLayerGroupType = Utilities.convertFeatureTypeToMapLayerGroupType(featureType);
        if (mapLayerGroupType != null) {
            this.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == mapLayerGroupType).forEach(m => {
                if (m.olLayers != null) {
                    m.olLayers.forEach(o => {
                        o.setVisible(visible);
                    });
                }
            });
        }
        
        // save to local settings
        this.setLocalMapDetails_Visibility();
    }

    // ----------------------------------------------------------- //
    // MAP UTILS
    public exportMapToPng = (rect: JQuery<HTMLElement>, callback: exportMapToPngCallback): void => {
        const self = this;

        self.map.once('postcompose', function (event) {
            // Create a new canvas
            var newCanvas = document.createElement('canvas');
            newCanvas.width = rect[0].getBoundingClientRect().width;
            newCanvas.height = rect[0].getBoundingClientRect().height;
            var newContext = newCanvas.getContext('2d');
           
            // Also draw the scale bar
            var scaleBarEl = $('.ol-scale-bar');
            var mapextent: Extent = [0,0,0,0];
           
            // Render the scale bar
            (html2canvas as any)(scaleBarEl[0], { backgroundColor: "rgba(0,0,0,0)" }).then(function (scaleBarCanvas: HTMLCanvasElement) {
                // Get the main map canvases 
                $('.ol-layer canvas').each(function (i, el: HTMLCanvasElement) {
                    processCanvas(el);
                });

                function processCanvas(el: HTMLCanvasElement) {
                    // Go through each of the canvas layers
                    if (el.width > 0) {
                        // Check the transform to see if any of them are being scaled
                        var sx = rect[0].getBoundingClientRect().left - el.getBoundingClientRect().left;
                        var sy = rect[0].getBoundingClientRect().top - el.getBoundingClientRect().top;
                        var transform = el.style.transform;
                    
                        var matrix = transform == "" ? [1,0,0,1,0,0] : transform.match(/^matrix\(([^\(]*)\)$/)[1].split(',').map(Number);
                        //CanvasRenderingContext2D.prototype.setTransform.apply(newContext, matrix);
                        newContext.drawImage(
                            el,
                            sx / matrix[0], sy / matrix[3],
                            newCanvas.width / matrix[0], newCanvas.height / matrix[3],
                            0, 0,
                            newCanvas.width, newCanvas.height
                        );

                        // Get map extent
                        var xmin_pixel = rect[0].getBoundingClientRect().left - el.getBoundingClientRect().left;
                        var ymax_pixel = rect[0].getBoundingClientRect().top - el.getBoundingClientRect().top;
                        var xmax_pixel = xmin_pixel + rect[0].getBoundingClientRect().width;
                        var ymin_pixel = ymax_pixel + rect[0].getBoundingClientRect().height;
                        var TL = self.map.getCoordinateFromPixel([xmin_pixel, ymax_pixel]);
                        var BR = self.map.getCoordinateFromPixel([xmax_pixel, ymin_pixel]);
                        mapextent = [TL[0].toFixed(0) as any, BR[1].toFixed(0) as any, BR[0].toFixed(0) as any, TL[1].toFixed(0) as any];
                    }
                }

                newContext.drawImage(scaleBarCanvas, newCanvas.width - scaleBarCanvas.width - 10, newCanvas.height - scaleBarCanvas.height-10);

                // Create the image and then callback
                newCanvas.toBlob(function (blob) {
                    callback(blob, mapextent);
                });
            });
        });

        // Render the map
        self.map.renderSync();
    }

    public undoLastPoint = (): void => {
        this.MapInteraction.undoLastPoint();
    }

    public showLegendModal = (): void => {
        const self = this;
        Utilities.BlockUI();
        $('#placeholder-for-modals').load(window.G_SITEFOLDERNAME +'/MapActions/ShowMapLegend', function (response, status, xhr) {
            if (xhr.status === 401) {//redirect to login in unauthorised
                window.location.href = '/account/login?ReturnUrl=' + encodeURIComponent(window.location.pathname);
            }
            Utilities.UnBlockUI();
            Utilities.GetModal('#mapLegendModal', ModalAction.Create);
            self.mapLegend.createLegendInModal();
        });
    }

    // ****************************************************************** //
    // SCALE
    // ****************************************************************** //
    public getCurrentScale = (round: Boolean): number => {
        const self = this;
        
        var INCHES_PER_UNIT = { 'm': 39.37, 'dd': 4374754 };
        var DOTS_PER_INCH = 72;

        var units = self.map.getView().getProjection().getUnits();
        var resolution = self.map.getView().getResolution();

        var scale = INCHES_PER_UNIT[units] * DOTS_PER_INCH * resolution;
        if (round) {
            scale = Math.round(scale);
        }

        // MAY NOT BE CORRECT
        return scale;
    }

    // ****************************************************************** //
    // WKT
    // ****************************************************************** //
    private createFeatureFromWKT = (wktStr: string, allowedGeomTypes: Array<GeometryType>): Feature => {
        const self = this;

        var f: Feature = null;
        try {
            let wkt = new WKT();
            var tmpF = wkt.readFeature(wktStr, {
                dataProjection: 'EPSG:27700',
                featureProjection: self.mapConfigModel.mapInitialState.mapBaseProjection
            });

            if (allowedGeomTypes.indexOf(tmpF.getGeometry().getType()) != -1) f = tmpF;
        }
        catch (e) { console.log(e); }

        // If there is an error, return null
        return f;
    } 

    // ****************************************************************** //
    // MAP LAYER VISIBILITY
    // ****************************************************************** //
    public bindMapLayersToUI = (): void => {
        var self = this;
        $('[data-maplayergrouptype]').each(function () {
            var el = $(this);
            var mapLayerGroupType = el.data('maplayergrouptype');
            let legendname = el.data('legendname');
            var layerId = el.data('coremaplayer');

            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == mapLayerGroupType).forEach(c => {
                // This will make sure we select the right config angd match it to the right HTML map layer element
                if (layerId!= null && layerId == c.layerID) {
                    if (c.visibleOnStartup == true && c.enabled == true) el.find('input').prop('checked', true);
                    // check auth policies
                    if (self.mapLayersHelper.checkAuthPolicies(c, self.mapConfigModel.contactRef) == false) {
                        var x = 1;
                        el.hide()
                    };
                }
            });

            self.mapConfigModel.additionalLayers.forEach(c => {
                // Check if this is an additional layer
                var configAdditional = self.mapConfigModel.additionalLayers.find(c => c.legendStyle.legendName == legendname);
                if (configAdditional != null) {

                    if (configAdditional.visibleOnStartup == true && configAdditional.enabled == true) {
                        el.find('input').prop('checked', true);
                    }
                    var layer = self.additionalMapLayersList.find(x => x.DisplayName == configAdditional.legendStyle.legendName);

                    if (layer && layer.CanInfoClick == true) {

                        el.find('[name="additionalLayerInfoBtn"]').on('click', function () {
                            events.bus.publish(events.eventResetAllButtons(false));
                            events.bus.publish(events.eventRemoveAllMapInteractions());
                            events.bus.publish(events.eventClearAllHighlights());
                            events.bus.publish(events.eventClearSelectedFeatures());
                            $('[name="additionalLayerInfoBtn"]').css("color", "black");
                            $(this).css("color", "red");

                            events.bus.publish(events.eventStartAdditionalLayersLocationInfo(layer));
                        });
                    }
                    else {
                        el.find('[name="additionalLayerInfoBtn"]').css("display", "none");
                    }
                }
            });

            // Btn click
            el.find('[name="btnSwitch"]').on('click', function () {
                let checked = $(this).find('input').prop('checked');
                $(this).find('input').prop('checked', !checked);
                self.toggleMapLayerVisibility(el);
            });
        });
    }

    public toggleMapLayerVisibility = (el: JQuery<HTMLElement>, updateLocalSettings: boolean = true): void => {
        const self = this;

        let mapLayergroupType = el.data('maplayergrouptype');
        let layergroupname = el.data('layergroupname');
        let checked = el.find('input').prop('checked');
        let layerID = el.data('coremaplayer');
        let layers = [];
        
        // Check the layer group.
        // If the zoom level less than allowed, switch off the visiblity of the group and disable the switch button
        // If the zoom level is allowed, check to see if the layer is already visible/not visible 
        if ((<any>Object).values(MapLayerGroupType).includes(mapLayergroupType) && mapLayergroupType != MapLayerGroupType.User) {
            toggle(mapLayergroupType, layerID, layergroupname);
        }
        else {
            // These are the 'additional' layers
            self.mapConfigModel.additionalLayers.forEach(a => {
                let legendname = el.data('legendname');
                if (mapLayergroupType == a.mapLayerGroupType && a.legendStyle.legendName == legendname) {
                    layers = [self.additionalMapLayersList.find(x => x.DisplayName == a.legendStyle.legendName)];
                    if (self.map && self.map.getView().getZoom() > a.minZoom) { show(el); if (layers[0].getVisible() != checked) layers.forEach(l => l.setVisible(checked)); } else { hide(el); layers.forEach(l => l.setVisible(false)); }
                }
            });
        }

        // save to local settings
        if (updateLocalSettings == true) self.setLocalMapDetails_Visibility();

        function toggle(type: MapLayerGroupType, layerID: string, layergroupname: string) {
            self.mapConfigModel.coreLayers.filter(c => c.mapLayerGroupType == type).forEach(l => {
                if (layerID != null && layerID == l.layerID && l.olLayers != null) {
                    l?.olLayers.forEach(o => {
                        if (self.map && self.map.getView().getZoom() > l.minZoom) {
                            show(el);
                            if (o.getVisible() != checked) o.setVisible(checked);
                        } else { hide(el); o.setVisible(false); }

                    });
                }
            });
        }

        function show(el) {
            el.find('[name="btnSwitch"]').css('display', 'block');
            el.find('[name="visibleIcon"]').css('display', 'none');
        }

        function hide(el) {
            el.find('[name="btnSwitch"]').css('display', 'none');
            el.find('[name="visibleIcon"]').css('display', 'block');
        }
    }

    public setLocalMapDetails_Visibility = (): void => {
        var self = this;
        let localStorageSettings_str = window.localStorage.getItem('localStorageSettings');
        let localStorageSettings: LocalStorageSettingsModel = JSON.parse(localStorageSettings_str);

        localStorageSettings.Layers = [];
        this.mapConfigModel.coreLayers.forEach(l => {
            if (self.mapLayersHelper.checkAuthPolicies(l, self.mapConfigModel.contactRef) == true) {
                var layerKey = l.layerGroupName + "_" + l.legendName;
                if (l && l.olLayers && l.olLayers.length > 0) localStorageSettings.Layers.push({ Visible: l.olLayers[0].getVisible(), LayerKey: layerKey });
            }
        });

        window.localStorage.setItem('localStorageSettings', JSON.stringify(localStorageSettings));
    }
}




